import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CoreApiService } from './core-api.service';
import PlaceSearchRequest = google.maps.places.PlaceSearchRequest;
import PlaceResult = google.maps.places.PlaceResult;
import AutocompletionRequest = google.maps.places.AutocompletionRequest;
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import PlaceDetailsRequest = google.maps.places.PlaceDetailsRequest;

export interface UserLocation {
  city: string;
  country: string;
  ip: string;
  postal: string;
  region: string;
  location: google.maps.LatLngLiteral;
}

export interface UserGeolocation {
  coords: google.maps.LatLngLiteral;
  mode: GeolocationMode;
}

enum GeolocationMode {
  Default,
  HighAccuracy,
  Ip,
}

type Bounds = google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;

@Injectable({
  providedIn: 'root',
})
export class PlacesService {
  boundsChange$: Observable<Bounds | null>;
  locationChange$: Observable<UserGeolocation | null>;

  private bounds!: Bounds;
  private radius = 10000;
  private location!: UserGeolocation;
  private _autocompleteService?: google.maps.places.AutocompleteService;
  private _placesService?: google.maps.places.PlacesService;

  // Constructed lazily: the maps APP_INITIALIZER resolves even when the
  // script fails to load (bad key, ad blocker), so `google` may not exist.
  // Constructing eagerly here crashed every component that merely injects
  // this service; lazily, only an actual places lookup can fail.
  private get autocompleteService(): google.maps.places.AutocompleteService {
    return (this._autocompleteService ??= new google.maps.places.AutocompleteService());
  }

  private get placesService(): google.maps.places.PlacesService {
    return (this._placesService ??= new google.maps.places.PlacesService(
      document.createElement('div'),
    ));
  }

  private boundsChange: BehaviorSubject<Bounds | null> =
    new BehaviorSubject<Bounds | null>(null);
  private locationChange: BehaviorSubject<UserGeolocation | null> =
    new BehaviorSubject<UserGeolocation | null>(null);

  constructor(private coreApiService: CoreApiService, private zone: NgZone) {
    this.setBoundsByCurrentLocation();
    this.boundsChange$ = this.boundsChange.asObservable();
    this.locationChange$ = this.locationChange.asObservable();
  }

  setBounds(bounds: Bounds) {
    this.bounds = bounds;
    this.boundsChange.next(bounds);
  }

  setRadius(radius: number) {
    this.radius = radius;
  }

  setLocation(location: google.maps.LatLngLiteral, mode: GeolocationMode) {
    this.location = {
      coords: location,
      mode,
    };
    this.locationChange.next(this.location);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  setBoundsByCurrentLocation(mode: GeolocationMode = GeolocationMode.Default) {
    switch (mode) {
      case GeolocationMode.Default:
      case GeolocationMode.HighAccuracy:
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            position => {
              const geolocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              const accuracy =
                position.coords.accuracy < this.radius
                  ? this.radius
                  : position.coords.accuracy;
              const circle = new google.maps.Circle({
                center: geolocation,
                radius: accuracy,
              });

              const bounds = circle.getBounds();
              if (bounds) {
                this.setBounds(bounds);
              }
              this.setLocation(geolocation, mode);

              if (mode === GeolocationMode.Default) {
                this.setBoundsByCurrentLocation(GeolocationMode.HighAccuracy);
              }
            },
            () => {
              if (mode === GeolocationMode.Default) {
                this.setBoundsByCurrentLocation(GeolocationMode.Ip);
              }
            },
            {
              enableHighAccuracy: mode === GeolocationMode.HighAccuracy,
            },
          );
        } else {
          this.setBoundsByCurrentLocation(GeolocationMode.Ip);
        }
        break;
      case GeolocationMode.Ip:
        this.coreApiService.getMyLocation().subscribe((userLocation: UserLocation) => {
          const geolocation = {
            lat: userLocation.location.lat,
            lng: userLocation.location.lng,
          };
          const circle = new google.maps.Circle({
            center: geolocation,
            radius: this.radius,
          });

          const bounds = circle.getBounds();
          if (bounds) {
            this.setBounds(bounds);
          }
          this.setLocation(geolocation, mode);
        });
        break;
    }
  }

  getNearPlaces(
    request?: PlaceSearchRequest,
    map?: google.maps.Map,
  ): Observable<PlaceResult[]> {
    return new Observable(observer => {
      if (!this.bounds) {
        this.zone.run(() => {
          observer.next([]);
          observer.complete();
        });

        return;
      }
      const service = new google.maps.places.PlacesService(
        map ? map : document.createElement('div'),
      );
      service.nearbySearch(
        {
          bounds: this.bounds,
          ...request,
        },
        (resp: PlaceResult[] | null) => {
          this.zone.run(() => {
            observer.next(resp || []);
            observer.complete();
          });
        },
      );
    });
  }

  getPlaceDetails(place: AutocompletePrediction, request: Partial<PlaceDetailsRequest>) {
    return new Observable(observer => {
      this.placesService.getDetails(
        {
          fields: ['geometry', 'id', 'name', 'formatted_address'],
          ...request,
          placeId: place.place_id,
        },
        (details: google.maps.places.PlaceResult | null) => {
          this.zone.run(() => {
            observer.next(details);
            observer.complete();
          });
        },
      );
    });
  }

  searchPlaces(
    value?: string,
    request?: AutocompletionRequest,
  ): Observable<AutocompletePrediction[]> {
    return new Observable(observer => {
      this.autocompleteService.getPlacePredictions(
        {
          bounds: this.bounds,
          // @ts-expect-error
          input: value,
          ...request,
        },
        (resp: AutocompletePrediction[]) =>
          this.zone.run(() => {
            observer.next(resp);
            observer.complete();
          }),
      );
    });
  }
}
